// src/features/dashboard/admin/tutor/TutorImportModal.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { fetchDistinctClasses, fetchStudentsByClass, bulkAddStudents } from '../../../../services/sheetDataService';
import Modal from '../../../../components/ui/Modal';



const TutorImportModal = ({ isOpen, onClose, onImportComplete }) => {
  const { user, accessToken } = useAuth();
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [sourceStudents, setSourceStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const loadClasses = async () => {
      try {
        const classes = await fetchDistinctClasses(accessToken, user.spreadsheetId);
        setAvailableClasses(classes);
      } catch (err) {
        console.error(err);
      }
    };
    loadClasses();
    setSelectedClass(null);
    setSourceStudents([]);
    setSelectedStudents({});
  }, [isOpen, accessToken, user]);

  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedClass || !accessToken || !user?.spreadsheetId) return;
      setLoading(true);
      try {
        const students = await fetchStudentsByClass(
          accessToken,
          user.spreadsheetId,
          selectedClass.subject,
          selectedClass.grade
        );
        setSourceStudents(students);
        const initial = {};
        students.forEach(s => { initial[s.userId] = true; });
        setSelectedStudents(initial);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadStudents();
  }, [selectedClass, accessToken, user]);

  const toggleSelectAll = (checked) => {
    const newSelection = {};
    sourceStudents.forEach(s => { newSelection[s.userId] = checked; });
    setSelectedStudents(newSelection);
  };

  const toggleStudent = (userId) => {
    setSelectedStudents(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleImport = async () => {
    const studentsToImport = sourceStudents.filter(s => selectedStudents[s.userId]);
    if (studentsToImport.length === 0) {
      alert('Please select at least one student.');
      return;
    }

    setImporting(true);
    try {
      // For each imported student, we need to add them as a tutoring student.
      // We'll use bulkAddStudents with studentType: 'tutor' and a custom schedule.
      // Since we don't have a custom schedule per student, we'll ask the teacher to set it later.
      // We'll set an empty schedule (or a default one-session placeholder).
      const metadata = {
        organisationId: user.organisationId || 'N/A',
        school: user.school || 'N/A',
        province: user.province || 'N/A',
        district: user.district || 'N/A',
        circuit: user.circuit || 'N/A',
        teacher: user.email,
        subject: 'N/A', // will be set per student? We'll keep the source subject? Better to let tutor assign later.
        grade: 'N/A',
        googleClassroomLink: '',
        schedule: [{ day: 'Monday', startTime: '09:00', duration: 60 }], // default, teacher will edit
        zoomLink: '',
        zoomPassword: '',
        studentType: 'tutor',
      };

      const studentsArray = studentsToImport.map(s => ({
        name: s.name,
        surname: s.surname,
        email: s.email,
      }));

      await bulkAddStudents(accessToken, user.spreadsheetId, studentsArray, metadata);

      alert(`✅ Imported ${studentsArray.length} student(s) as tutoring students.`);
      if (onImportComplete) onImportComplete();
      onClose();
    } catch (err) {
      alert('❌ Failed to import students: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <h2 className="text-2xl font-bold mb-4">Import Students as Tutoring</h2>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Source Class</label>
        <select
          value={selectedClass ? `${selectedClass.subject}|${selectedClass.grade}` : ''}
          onChange={(e) => {
            const [subject, grade] = e.target.value.split('|');
            const cls = availableClasses.find(c => c.subject === subject && c.grade === grade);
            setSelectedClass(cls || null);
          }}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">Select a class</option>
          {availableClasses.map(cls => (
            <option key={`${cls.subject}|${cls.grade}`} value={`${cls.subject}|${cls.grade}`}>
              {cls.subject} - {cls.grade}
            </option>
          ))}
        </select>
      </div>

      {selectedClass && (
        <>
          {loading ? (
            <p>Loading students...</p>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={sourceStudents.length > 0 && sourceStudents.every(s => selectedStudents[s.userId])}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
                <label className="text-sm font-medium">Select All ({sourceStudents.length})</label>
              </div>
              <div className="border rounded max-h-60 overflow-y-auto">
                {sourceStudents.map(s => (
                  <div key={s.userId} className="flex items-center gap-2 p-2 border-b">
                    <input
                      type="checkbox"
                      checked={!!selectedStudents[s.userId]}
                      onChange={() => toggleStudent(s.userId)}
                    />
                    <span>{s.name} {s.surname}</span>
                    <span className="text-sm text-gray-500 ml-2">{s.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400" disabled={importing}>
          Cancel
        </button>
        <button
          onClick={handleImport}
          disabled={!selectedClass || importing || sourceStudents.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {importing ? 'Importing...' : 'Import as Tutoring Students'}
        </button>
      </div>
    </Modal>
  );
};

export default TutorImportModal;